import React, { useEffect, useState } from 'react';
import { MessageSquare, Loader2, HelpCircle, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface SalesAgentProps {
  profile: any;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

interface CRMConfig {
  crm_full_auto_mode: boolean;
  crm_auto_mode_on_sleeping: boolean;
  timezone?: number;
}

export const SalesAgent: React.FC<SalesAgentProps> = ({ profile, setError, setSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [crmConfig, setCrmConfig] = useState<CRMConfig>({
    crm_full_auto_mode: false,
    crm_auto_mode_on_sleeping: false
  });
  const [updatingCRMMode, setUpdatingCRMMode] = useState(false);
  const [updatingNonWorkingMode, setUpdatingNonWorkingMode] = useState(false);
  
  useEffect(() => {
    if (profile) {
      fetchCRMConfig();
    }
  }, [profile]);
  
  const fetchCRMConfig = async () => {
    try {
      setLoading(true);
      
      if (profile?.id_company) {
        const { data, error } = await supabase
          .from('config')
          .select('*')
          .eq('id_company', profile.id_company)
          .maybeSingle();
        
        if (error && error.code !== 'PGRST116') throw error;
        
        if (data) {
          setCrmConfig({
            crm_full_auto_mode: data.crm_full_auto_mode || false,
            crm_auto_mode_on_sleeping: data.crm_auto_mode_on_sleeping || false,
            timezone: data.timezone
          });
        }
      }
    } catch (err) {
      console.error('Error fetching CRM config:', err);
      setError(err instanceof Error ? err.message : 'Error loading CRM configuration');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCRMModeChange = async (crm_full_auto_mode: boolean) => {
    if (!profile?.id_company || updatingCRMMode) return;

    setUpdatingCRMMode(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('Updating CRM mode:', {
        id_company: profile.id_company,
        crm_full_auto_mode: crm_full_auto_mode,
        id: 1
      });

      const { error } = await supabase
        .from('config')
        .update({ crm_full_auto_mode: crm_full_auto_mode })
        .eq('id_company', profile.id_company);

      if (error) {
        console.error('Supabase CRM update error:', error);
        throw error;
      }

      setCrmConfig(prev => ({ ...prev, crm_full_auto_mode: crm_full_auto_mode }));
      setSuccess(`Modo ${crm_full_auto_mode ? 'autónomo' : 'semi-autónomo'} activado correctamente`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating CRM mode:', err);
      setError('Error al actualizar el modo de operación del CRM');
    } finally {
      setUpdatingCRMMode(false);
    }
  };

  const handleNonWorkingModeChange = async (enabled: boolean) => {
    if (!profile?.id_company || updatingNonWorkingMode) return;

    setUpdatingNonWorkingMode(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('Updating non-working mode:', {
        id_company: profile.id_company,
        enabled: enabled,
        id: 1
      });

      const { error } = await supabase
        .from('config')
        .update({ crm_auto_mode_on_sleeping: enabled })
        .eq('id_company', profile.id_company);

      if (error) {
        console.error('Supabase non-working mode update error:', error);
        console.error('Supabase non-working mode update error details:', { error, table: 'config', action: 'update' });
        throw error;
      }

      setCrmConfig(prev => ({ ...prev, crm_auto_mode_on_sleeping: enabled }));
      setSuccess(`Modo autónomo en horarios no laborables ${enabled ? 'activado' : 'desactivado'} correctamente`);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating non-working hours mode:', err);
      setError('Error al actualizar la configuración del modo no laboral');
    } finally {
      setUpdatingNonWorkingMode(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm mb-6">
      <div className="p-6 border-b">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-6 h-6 text-gray-700" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Agente de Ventas</h2>
            <p className="text-sm text-gray-500">
              Configura el modo de operación del CRM
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <div className="relative flex items-center">
                    <input
                      id="autonomous_mode"
                      type="radio"
                      name="crm_mode"
                      checked={crmConfig.crm_full_auto_mode}
                      onChange={() => handleCRMModeChange(true)}
                      className="h-4 w-4 rounded-full border-gray-300 opacity-0 absolute"
                    />
                    <div className={`h-4 w-4 rounded-full border ${crmConfig.crm_full_auto_mode ? 'border-blue-500' : 'border-gray-300'} flex items-center justify-center`}>
                      {crmConfig.crm_full_auto_mode && <div className="h-2 w-2 rounded-full bg-blue-500"></div>}
                    </div>
                  </div>
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="autonomous_mode" className="font-medium text-gray-900">Modo autónomo</label>
                  <p className="text-gray-500">La IA responderá automáticamente los mensajes de todos los clientes</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <div className="relative flex items-center">
                    <input
                      id="semi_autonomous_mode"
                      type="radio"
                      name="crm_mode"
                      checked={!crmConfig.crm_full_auto_mode}
                      onChange={() => handleCRMModeChange(false)}
                      className="h-4 w-4 rounded-full border-gray-300 opacity-0 absolute"
                    />
                    <div className={`h-4 w-4 rounded-full border ${!crmConfig.crm_full_auto_mode ? 'border-blue-500' : 'border-gray-300'} flex items-center justify-center`}>
                      {!crmConfig.crm_full_auto_mode && <div className="h-2 w-2 rounded-full bg-blue-500"></div>}
                    </div>
                  </div>
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="semi_autonomous_mode" className="font-medium text-gray-900">Modo semi-autónomo</label>
                  <p className="text-gray-500">La IA generará automáticamente una respuesta a los mensajes entrantes, pero no la enviará hasta que un vendedor lo confirme</p>
                </div>
              </div>
              
              <div className="pl-6 border-l-2 border-gray-100">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <div className="relative flex items-center">
                      <input
                        id="non_working_hours_mode"
                        type="checkbox"
                        checked={crmConfig.crm_auto_mode_on_sleeping}
                        onChange={(e) => handleNonWorkingModeChange(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 opacity-0 absolute"
                      />
                      <div className={`h-4 w-4 rounded border ${crmConfig.crm_auto_mode_on_sleeping ? 'bg-blue-500 border-blue-500' : 'border-gray-300'} flex items-center justify-center`}>
                        {crmConfig.crm_auto_mode_on_sleeping && <Check className="h-3 w-3 text-white" />}
                      </div>
                    </div>
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="non_working_hours_mode" className="font-medium text-gray-900">Activar modo autónomo en horarios no laborables</label>
                    <p className="text-gray-500">La IA responderá automáticamente los mensajes de todos los clientes durante los horarios y días no laborables. (Puedes editar los horarios de tu negocio en Configuración &gt; Horarios)</p>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
        )}
      </div>
    </div>
  );
};